import React, { useState } from 'react';
import { Play, Code, Database, Globe, Zap, CheckCircle, AlertCircle } from 'lucide-react';
import { autonomousApplicationGenerator } from '../../services/autonomousApplicationGenerator';
import { toast } from 'sonner';

interface AutonomousGeneratorProps {
  onApplicationGenerated?: (application: any) => void;
}

export function AutonomousGenerator({ onApplicationGenerated }: AutonomousGeneratorProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState<string>('');
  const [generatedApplication, setGeneratedApplication] = useState<any>(null);
  const [requirements, setRequirements] = useState({
    description: '',
    domain: 'general',
    userType: 'individual' as 'individual' | 'business' | 'enterprise',
    features: [] as string[],
    technologies: {
      frontend: ['react'],
      backend: ['nodejs'],
      database: ['postgresql'],
      deployment: ['docker']
    },
    constraints: {
      budget: undefined as number | undefined,
      timeline: undefined as string | undefined,
      scalability: 'medium' as 'low' | 'medium' | 'high',
      security: 'standard' as 'basic' | 'standard' | 'enterprise',
      compliance: [] as string[]
    },
    targetPlatforms: ['web'] as ('web' | 'mobile' | 'desktop' | 'api')[]
  });

  const handleGenerateApplication = async () => {
    if (!requirements.description.trim()) {
      toast.error('Please provide a description for your application');
      return;
    }

    setIsGenerating(true);
    setGenerationProgress('Analyzing requirements...');

    try {
      // Step 1: Analyze requirements
      setGenerationProgress('Analyzing natural language requirements...');

      // Step 2: Generate application
      setGenerationProgress('Generating application architecture...');
      const application = await autonomousApplicationGenerator.generateApplication(requirements);

      setGeneratedApplication(application);
      onApplicationGenerated?.(application);

      toast.success(`Application "${application.name}" generated successfully!`);

    } catch (error) {
      console.error('Application generation failed:', error);
      toast.error('Failed to generate application. Please try again.');
    } finally {
      setIsGenerating(false);
      setGenerationProgress('');
    }
  };

  const addFeature = (feature: string) => {
    if (feature && !requirements.features.includes(feature)) {
      setRequirements(prev => ({
        ...prev,
        features: [...prev.features, feature]
      }));
    }
  };

  const removeFeature = (feature: string) => {
    setRequirements(prev => ({
      ...prev,
      features: prev.features.filter(f => f !== feature)
    }));
  };

  const updateTechnology = (category: keyof typeof requirements.technologies, tech: string[]) => {
    setRequirements(prev => ({
      ...prev,
      technologies: {
        ...prev.technologies,
        [category]: tech
      }
    }));
  };

  const togglePlatform = (platform: 'web' | 'mobile' | 'desktop' | 'api') => {
    setRequirements(prev => ({
      ...prev,
      targetPlatforms: prev.targetPlatforms.includes(platform)
        ? prev.targetPlatforms.filter(p => p !== platform)
        : [...prev.targetPlatforms, platform]
    }));
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Autonomous Application Generator
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Generate complete applications from natural language descriptions
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-sm text-gray-500">
              Powered by MAWI AI
            </div>
            <Zap className="w-5 h-5 text-blue-500" />
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Requirements Input */}
        <div className="w-1/2 border-r border-gray-200 dark:border-gray-700 flex flex-col">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Application Requirements
            </h2>

            {/* Description */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Application Description
              </label>
              <textarea
                value={requirements.description}
                onChange={(e) => setRequirements(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe your application in natural language..."
                className="w-full h-32 p-3 border border-gray-300 dark:border-gray-600 rounded-lg resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>

            {/* Features */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Key Features
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {requirements.features.map(feature => (
                  <span
                    key={feature}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm"
                  >
                    {feature}
                    <button
                      onClick={() => removeFeature(feature)}
                      className="hover:text-red-600"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                placeholder="Add a feature..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addFeature((e.target as HTMLInputElement).value);
                    (e.target as HTMLInputElement).value = '';
                  }
                }}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>

            {/* Technologies */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Frontend
                </label>
                <select
                  multiple
                  value={requirements.technologies.frontend}
                  onChange={(e) => updateTechnology('frontend', Array.from(e.target.selectedOptions, opt => opt.value))}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="react">React</option>
                  <option value="vue">Vue.js</option>
                  <option value="angular">Angular</option>
                  <option value="svelte">Svelte</option>
                  <option value="nextjs">Next.js</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Backend
                </label>
                <select
                  multiple
                  value={requirements.technologies.backend}
                  onChange={(e) => updateTechnology('backend', Array.from(e.target.selectedOptions, opt => opt.value))}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="nodejs">Node.js</option>
                  <option value="python">Python</option>
                  <option value="java">Java</option>
                  <option value="csharp">C#</option>
                  <option value="golang">Go</option>
                </select>
              </div>
            </div>

            {/* Target Platforms */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Target Platforms
              </label>
              <div className="flex gap-2">
                {(['web', 'mobile', 'desktop', 'api'] as const).map(platform => (
                  <button
                    key={platform}
                    onClick={() => togglePlatform(platform)}
                    className={`px-4 py-2 rounded capitalize ${
                      requirements.targetPlatforms.includes(platform)
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {platform}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleGenerateApplication}
              disabled={isGenerating || !requirements.description.trim()}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
            >
              {isGenerating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Generating Application...
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5" />
                  Generate Autonomous Application
                </>
              )}
            </button>

            {generationProgress && (
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm text-blue-700 dark:text-blue-300">
                    {generationProgress}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Generated Application */}
        <div className="w-1/2 flex flex-col">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Generated Application
            </h2>
          </div>

          <div className="flex-1 overflow-auto p-6">
            {generatedApplication ? (
              <div className="space-y-6">
                {/* Application Overview */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border">
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {generatedApplication.name}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        {generatedApplication.description}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <span className="text-sm text-gray-500">Confidence</span>
                      <div className="text-lg font-semibold text-green-600">
                        {Math.round(generatedApplication.confidence * 100)}%
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Estimated Cost</span>
                      <div className="text-lg font-semibold text-blue-600">
                        ${generatedApplication.estimatedCost}
                      </div>
                    </div>
                  </div>

                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Timeline: {generatedApplication.estimatedTimeline}
                  </div>
                </div>

                {/* Architecture Overview */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border">
                  <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Application Architecture
                  </h4>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <span className="text-sm text-gray-500">Pattern</span>
                      <div className="font-medium capitalize">
                        {generatedApplication.architecture?.pattern}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Components</span>
                      <div className="font-medium">
                        {generatedApplication.architecture?.components?.length || 0} services
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h5 className="font-medium text-sm">Services:</h5>
                    <div className="grid grid-cols-2 gap-2">
                      {generatedApplication.architecture?.components?.map((component: any, index: number) => (
                        <div key={index} className="text-sm p-2 bg-gray-50 dark:bg-gray-700 rounded">
                          <div className="font-medium">{component.name}</div>
                          <div className="text-gray-600 dark:text-gray-400 capitalize">
                            {component.type} • {component.technology}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Database Schema */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border">
                  <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Database className="w-5 h-5" />
                    Database Schema
                  </h4>

                  <div className="mb-4">
                    <span className="text-sm text-gray-500">Type</span>
                    <div className="font-medium capitalize">
                      {generatedApplication.database?.type}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h5 className="font-medium text-sm">Tables:</h5>
                    {generatedApplication.database?.tables?.map((table: any, index: number) => (
                      <div key={index} className="text-sm p-3 bg-gray-50 dark:bg-gray-700 rounded">
                        <div className="font-medium">{table.name}</div>
                        <div className="text-gray-600 dark:text-gray-400">
                          {table.columns?.length || 0} columns • PK: {table.primaryKey}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* API Endpoints */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border">
                  <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Code className="w-5 h-5" />
                    API Endpoints
                  </h4>

                  <div className="space-y-2">
                    {generatedApplication.apis?.[0]?.endpoints?.slice(0, 5).map((endpoint: any, index: number) => (
                      <div key={index} className="text-sm p-2 bg-gray-50 dark:bg-gray-700 rounded flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs rounded font-medium ${
                          endpoint.method === 'GET' ? 'bg-green-100 text-green-800' :
                          endpoint.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                          endpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {endpoint.method}
                        </span>
                        <span className="font-mono">{endpoint.path}</span>
                        <span className="text-gray-600 dark:text-gray-400 truncate">
                          {endpoint.summary}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Deployment Config */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border">
                  <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Deployment Configuration
                  </h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-500">Platform</span>
                      <div className="font-medium capitalize">
                        {generatedApplication.deployment?.platform}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Services</span>
                      <div className="font-medium">
                        {generatedApplication.deployment?.services?.length || 0} containers
                      </div>
                    </div>
                  </div>
                </div>

                {/* Download/Export Options */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border">
                  <h4 className="text-lg font-semibold mb-4">Export Application</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <button className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-2">
                      <Code className="w-4 h-4" />
                      Download Code
                    </button>
                    <button className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center justify-center gap-2">
                      <Database className="w-4 h-4" />
                      Export Schema
                    </button>
                    <button className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 flex items-center justify-center gap-2">
                      <Globe className="w-4 h-4" />
                      Deploy Config
                    </button>
                    <button className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 flex items-center justify-center gap-2">
                      <Play className="w-4 h-4" />
                      Quick Deploy
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <Zap className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium mb-2">Ready to Generate</h3>
                  <p className="text-sm">
                    Describe your application requirements on the left to generate a complete application
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}